import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { rbacApi } from '../api/rbac.api';
import { toast } from 'sonner';
import useInLeadershipShell from '@/hooks/useInLeadershipShell';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import RbacScreenIntro from '../components/RbacScreenIntro';
import RolesCreateCard from '../components/RolesCreateCard';
import RolesTable from '../components/RolesTable';
import RolePermissionsDrawer from '../components/RolePermissionsDrawer';

export default function RolesPage() {
  const navigate = useNavigate();
  const lead = useInLeadershipShell();
  const permissionsPath = lead ? '/management/permissions' : '/admin/permissions';
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', description: '', system_role: 'doctor' });
  const [saving, setSaving] = useState(false);
  const [drillRole, setDrillRole] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        rbacApi.loadRoles(),
        rbacApi.listPermissions().catch(() => []),
      ]);
      setRoles(r);
      setPermissions(Array.isArray(p) ? p : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load roles');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const permTotal = permissions.length;
  const stats = useMemo(() => {
    const active = roles.filter((r) => r.is_active !== false).length;
    const system = roles.filter((r) => r.is_system).length;
    const avg = roles.length
      ? Math.round(roles.reduce((n, r) => n + (r.permission_codes || []).length, 0) / roles.length)
      : 0;
    return [
      { label: 'Roles', value: roles.length, hint: `${active} active` },
      { label: 'System roles', value: system, hint: 'IITH defaults' },
      { label: 'Custom roles', value: Math.max(0, roles.length - system), hint: 'Created here' },
      { label: 'Avg permissions', value: avg, hint: `of ${permTotal || '—'} catalogue` },
    ];
  }, [roles, permTotal]);

  const create = async () => {
    if (!form.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      await rbacApi.createRole(form);
      toast.success('Role created — map permissions next');
      setForm({ name: '', description: '', system_role: 'doctor' });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create role');
    }
    setSaving(false);
  };

  const toggle = async (role) => {
    try {
      await rbacApi.updateRole(role.role_key, { is_active: role.is_active === false });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    }
  };

  return (
    <div className={`${lead ? `${LEADERSHIP_CONTENT} space-y-5` : 'p-6 max-w-6xl mx-auto space-y-5'}`} data-testid="roles-page">
      <RbacScreenIntro
        lead={lead}
        title="Roles"
        subtitle="Define who can enter which portal. Each role inherits permissions that unlock screens and actions."
        stats={stats}
      />

      <div className="grid lg:grid-cols-12 gap-5">
        <div className="lg:col-span-4">
          <RolesCreateCard form={form} setForm={setForm} saving={saving} onCreate={create} />
        </div>
        <div className="lg:col-span-8">
          <RolesTable
            roles={roles}
            loading={loading}
            permissionTotal={permTotal}
            onToggle={toggle}
            onViewPermissions={setDrillRole}
          />
        </div>
      </div>

      <RolePermissionsDrawer
        open={Boolean(drillRole)}
        onClose={() => setDrillRole(null)}
        role={drillRole}
        permissions={permissions}
        permissionTotal={permTotal}
        onEditMapping={(role) => {
          setDrillRole(null);
          navigate(permissionsPath);
          try { sessionStorage.setItem('rbac.focusRoleKey', role.role_key); } catch (_) { /* ignore */ }
        }}
      />
    </div>
  );
}
